// src/mobile_pages/const/Order_new.jsx
import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Save,
  FileText,
  Calendar,
  User,
  Users,
  Paperclip
} from "lucide-react";
import ReferenceModal from "../../components/approval/ReferenceModal";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import { getWorkOrderDetail, createConstMeta, updateConstMeta } from "../../api/const";
import MobilePeopleCellLoader from "../../components/common/people/MobilePeopleCellLoader";
import MobileAttachmentBox from "../../components/common/papers/MobileAttachmentBox";

export default function WorkOrderNew() {
  const { id: projectId, dns } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const attachmentBoxRef = useRef(null);
  const isEditMode = Boolean(dns);

  const [requestPeople, setRequestPeople] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "",
    start_date: "",
    end_date: "",
    confirmer: null,
    confirmer_name: "",
    confirmer_company_name: "",
  });

  const [showConfirmerModal, setShowConfirmerModal] = useState(false);
  const [confirmerIds, setConfirmerIds] = useState([]);

  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    people: true,
    content: true,
    attachments: true,
  });

  // 수정 모드 → 데이터 로드
  useEffect(() => {
    if (!dns) return;

    const load = async () => {
      try {
        const data = await getWorkOrderDetail(dns);
        const meta = data.meta || {};

        setForm((prev) => ({
          ...prev,
          title: data.title || "",
          start_date: meta.start_date || "",
          end_date: meta.end_date || "",
          confirmer: data.approval?.approver_id || null,
          confirmer_name: data.approval?.approver_name || "",
          confirmer_company_name: meta.confirmer_company_name || "",
        }));

        if (data.approval) {
          setConfirmerIds([data.approval.approver_id]);
        }

        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.setContent(meta.instructions || "");
          }
        }, 200);

        // 첨부파일 로드 (기존 파일이 있다면)
        if (data.attachments && attachmentBoxRef.current) {
          const initialFiles = data.attachments.map(f => ({
            ...f,
            _type: "uploaded", // 이미 업로드된 파일
            name: f.filename || f.name,
          }));
          attachmentBoxRef.current.setItems(initialFiles);
        }
      } catch (err) {
        console.error("WorkOrder load failed:", err);
      }
    };

    load();
  }, [dns]);

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // 뒤로가기
  const handleBack = () => {
    if (window.confirm("작성 중인 내용이 저장되지 않습니다. 나가시겠습니까?")) {
      navigate(`/project/${projectId}/const/order`);
    }
  };

  // 저장
  const handleSave = async () => {
    if (!form.title.trim()) {
      alert("작업명을 입력해주세요.");
      return;
    }

    setSaving(true);
    try {
      const instructions = editorRef.current?.getContent() || "";

      const payload = {
        project: projectId,
        module: "const",
        doc_type: "order",
        layout: null,
        values: {
          dns: dns || null,
          doc_type: "workorder",
          title: form.title,
          confirmer: form.confirmer || null,
          confirmer_name: form.confirmer_name || "",
          meta: {
            start_date: form.start_date,
            end_date: form.end_date,
            instructions,
            confirmer_company_name: form.confirmer_company_name,
          },
        },
      };

      let currentDns = dns;
      if (isEditMode) {
        await updateConstMeta(dns, payload);
      } else {
        const created = await createConstMeta(payload);
        currentDns = created.dns || created.id; // 반환값에 dns가 있다고 가정
      }

      // 첨부파일 저장
      if (attachmentBoxRef.current && currentDns) {
        await attachmentBoxRef.current.save(currentDns);
      }

      alert("저장되었습니다.");
      navigate(`/project/${projectId}/const/order`);
    } catch (err) {
      console.error("저장 실패:", err);
      alert("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
      <main className="w-full px-4 pt-6 pb-24">
        {/* 헤더 */}
        <header className="mb-4">
          <p className="text-xs text-gray-500">시공관리 · 작업지시서</p>
          <h1 className="text-xl font-bold text-slate-900">
            {isEditMode ? "작업지시서 수정" : "새 작업지시서"}
          </h1>
        </header>

        {/* 검토자 선택 모달 */}
        <ReferenceModal
          isOpen={showConfirmerModal}
          onClose={() => setShowConfirmerModal(false)}
          selectedIds={confirmerIds}
          name="검토자 선택"
          onChange={(ids) => {
            const onlyOne = ids.slice(-1);
            setConfirmerIds(onlyOne);
          }}
          onSave={(ids, detailUsers) => {
            const sel = detailUsers?.[0];
            if (sel) {
              const fullName = sel.last_name + sel.first_name;
              setForm((prev) => ({
                ...prev,
                confirmer: sel.id,
                confirmer_name: fullName,
              }));
            }
            return true;
          }}
        />

        <div className="space-y-3">
          {/* 기본 정보 섹션 */}
          <Section
            title="기본 정보"
            expanded={expandedSections.basic}
            onToggle={() => toggleSection("basic")}
            icon={<FileText size={16} className="text-blue-500" />}
          >
            <div className="space-y-4">
              {/* 작업명 */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  작업명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="작업명을 입력하세요"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-blue-400 focus:outline-none"
                />
              </div>

              {/* 작성일 & 종료일 */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    <Calendar size={12} className="inline mr-1" />
                    작성일
                  </label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-blue-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    <Calendar size={12} className="inline mr-1" />
                    종료일
                  </label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-blue-400 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </Section>

          {/* 담당자 섹션 */}
          <Section
            title="담당자"
            expanded={expandedSections.people}
            onToggle={() => toggleSection("people")}
            icon={<Users size={16} className="text-purple-500" />}
          >
            <div className="space-y-4">
              {/* 검토자 */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  검토자
                </label>

                {/* 선택된 검토자 표시 */}
                {form.confirmer_name && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                      {form.confirmer_name}
                      <button
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, confirmer: null, confirmer_name: "" }))}
                        className="w-4 h-4 flex items-center justify-center rounded-full bg-blue-200 hover:bg-blue-300 transition-colors"
                      >
                        <span className="text-[10px] font-bold">✕</span>
                      </button>
                    </span>
                  </div>
                )}

                {/* 검토자 선택 버튼 */}
                <button
                  type="button"
                  onClick={() => setShowConfirmerModal(true)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 active:scale-[0.99] transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                      <User size={18} className="text-slate-500" />
                    </div>
                    <div className="text-left">
                      {form.confirmer_name ? (
                        <>
                          <p className="text-[13px] font-medium text-slate-800">
                            1명 선택됨
                          </p>
                          <p className="text-[11px] text-slate-400">
                            탭하여 수정
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-[13px] font-medium text-slate-500">
                            검토자를 선택하세요
                          </p>
                          <p className="text-[11px] text-slate-400">
                            탭하여 선택
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-slate-400" />
                </button>
              </div>

              {/* 시공 담당자 */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  시공 담당자
                </label>
                <MobilePeopleCellLoader
                  dns={dns}
                  value={requestPeople}
                  onChange={(list) => setRequestPeople(list)}
                />
              </div>
            </div>
          </Section>

          {/* 지시사항 섹션 */}
          <Section
            title="지시사항"
            expanded={expandedSections.content}
            onToggle={() => toggleSection("content")}
            icon={<FileText size={16} className="text-green-500" />}
          >
            <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
              <TinyMCEeditor height={400} editorRef={editorRef} />
            </div>
          </Section>

          {/* 첨부파일 섹션 */}
          <Section
            title="첨부자료"
            expanded={expandedSections.attachments}
            onToggle={() => toggleSection("attachments")}
            icon={<Paperclip size={16} className="text-orange-500" />}
          >
            <MobileAttachmentBox ref={attachmentBoxRef} />
          </Section>
        </div>
      </main>

      {/* 모바일 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all flex items-center justify-center gap-2"
          >
            <ChevronLeft size={18} />
            취소
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full h-11 rounded-full bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 active:scale-[0.97] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Save size={18} />
            {saving ? "저장 중..." : "저장"}
          </button>
        </div>
      </footer>
    </div>
  );
}

// 섹션 카드 컴포넌트
function Section({ title, expanded, onToggle, icon, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""
            }`}
        />
      </button>
      {expanded && <div className="p-4">{children}</div>}
    </section>
  );
}
